import React from "react";
import { AlertTriangle, CheckCircle2, Plus, X } from "lucide-react";
import { fmtDate } from "../utils/inventory.helpers";

function getTodayInputDate() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function ReceiptDetailModal({
  open,
  receiptDetail,
  receiptMessage,
  receiptPopup,
  closeReceiptPopup,
  receiveForm,
  isReceiptAlreadyReceived,
  updateReceiveNote,
  addSplit,
  removeSplit,
  updateSplit,
  submitReceive,
  receiving,
  setReceiptOpen,
  warehouseInventoryService,
}) {
  const popupType = receiptPopup?.type || "error";
  const PopupIcon = popupType === "success" ? CheckCircle2 : AlertTriangle;

  if (!open || !receiptDetail) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
      <div className="max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-[24px] bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[#f8dde1] px-6 py-4">
          <div>
            <h3 className="text-[18px] font-bold text-slate-800">Inbound Receipt</h3>
            <p className="mt-1 text-[13px] text-slate-500">
              {receiptDetail.receiptCode ||
                `INB-${String(receiptDetail.id).replace(/-/g, "").slice(0, 8).toUpperCase()}`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setReceiptOpen(false)}
            className="rounded-[10px] p-2 text-slate-500 hover:bg-slate-100"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[74vh] overflow-y-auto px-6 py-5">
          {receiptMessage ? (
            <div className="mb-4 rounded-[14px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-3 text-[13px] font-medium text-[#d86a78]">
              {receiptMessage}
            </div>
          ) : null}

          {!isReceiptAlreadyReceived ? (
            <div className="mb-4 rounded-[16px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 rounded-full bg-white p-2 text-[#d86a78] shadow-sm">
                  <AlertTriangle size={16} />
                </div>
                <div>
                  <div className="text-[13px] font-bold text-slate-800">Before receiving stock</div>
                  <div className="mt-1 text-[12px] leading-5 text-slate-600">
                    Do not keep Batch No empty. Do not set Expiry Date before or same as MFG Date. Received + Damaged must equal Qty. Total split Qty cannot exceed Expected Qty.
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="grid gap-4 md:grid-cols-4">
            <div className="rounded-[14px] border border-[#f2c6cc] p-4">
              <div className="text-[12px] font-semibold text-slate-500">Status</div>
              <div className="mt-2 text-[14px] font-semibold text-slate-800">
                {receiptDetail.status || "-"}
              </div>
            </div>
            <div className="rounded-[14px] border border-[#f2c6cc] p-4">
              <div className="text-[12px] font-semibold text-slate-500">Source Type</div>
              <div className="mt-2 text-[14px] font-semibold text-slate-800">
                {receiptDetail.source_type || "-"}
              </div>
            </div>
            <div className="rounded-[14px] border border-[#f2c6cc] p-4">
              <div className="text-[12px] font-semibold text-slate-500">Created At</div>
              <div className="mt-2 text-[14px] font-semibold text-slate-800">
                {fmtDate(receiptDetail.created_at)}
              </div>
            </div>
            <div className="rounded-[14px] border border-[#f2c6cc] p-4">
              <div className="text-[12px] font-semibold text-slate-500">Received At</div>
              <div className="mt-2 text-[14px] font-semibold text-slate-800">
                {fmtDate(receiptDetail.received_at)}
              </div>
            </div>
          </div>

          {!isReceiptAlreadyReceived ? (
            <div className="mt-5">
              <label className="mb-2 block text-[13px] font-semibold text-slate-600">
                Receive Note
              </label>
              <textarea
                rows={3}
                value={receiveForm.note}
                onChange={(e) => updateReceiveNote(e.target.value)}
                placeholder="Optional note while receiving"
                className="w-full rounded-[14px] border border-[#f2c6cc] px-4 py-3 text-[14px] outline-none focus:border-[#d86a78] focus:ring-2 focus:ring-[#f9dfe4]"
              />
            </div>
          ) : null}

          <div className="mt-5 space-y-4">
            {receiveForm.items.map((item, itemIndex) => (
              <div
                key={item.receiptItemId}
                className="rounded-[18px] border border-[#f2c6cc] bg-[#fffdfd] p-4"
              >
                <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="text-[15px] font-semibold text-slate-800">
                      {item.productName || "Product"}
                    </div>
                    <div className="mt-1 text-[12px] text-slate-500">
                      SKU: {item.sku || "-"} | Expected Qty: {item.expectedQty}
                    </div>
                  </div>

                  {!isReceiptAlreadyReceived ? (
                    <button
                      type="button"
                      onClick={() => addSplit(itemIndex)}
                      className="inline-flex items-center gap-2 rounded-[12px] border border-[#f2c6cc] px-4 py-2 text-[12px] font-semibold text-slate-600 hover:bg-slate-50"
                    >
                      <Plus size={14} />
                      Add Split
                    </button>
                  ) : null}
                </div>

                <div className="mt-4 space-y-3">
                  {item.splits.map((split, splitIndex) => (
                    <div
                      key={`${item.receiptItemId}-${splitIndex}`}
                      className="grid gap-3 rounded-[14px] border border-[#f7d8de] bg-white p-4 md:grid-cols-7"
                    >
                      <div>
                        <label className="mb-1 block text-[12px] font-semibold text-slate-500">
                          Qty
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={split.qty}
                          disabled={isReceiptAlreadyReceived}
                          onChange={(e) =>
                            updateSplit(itemIndex, splitIndex, "qty", e.target.value)
                          }
                          className="h-[40px] w-full rounded-[10px] border border-[#f2c6cc] px-3 text-[13px] outline-none disabled:bg-slate-50 disabled:text-slate-500"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-[12px] font-semibold text-slate-500">
                          Received
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={split.receivedQty ?? ""}
                          disabled={isReceiptAlreadyReceived}
                          onChange={(e) =>
                            updateSplit(itemIndex, splitIndex, "receivedQty", e.target.value)
                          }
                          className="h-[40px] w-full rounded-[10px] border border-[#f2c6cc] px-3 text-[13px] outline-none disabled:bg-slate-50 disabled:text-slate-500"
                        />
                      </div>



                      <div>
                        <label className="mb-1 block text-[12px] font-semibold text-slate-500">
                          Damaged
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={split.damagedQty ?? ""}
                          disabled={isReceiptAlreadyReceived}
                          onChange={(e) =>
                            updateSplit(itemIndex, splitIndex, "damagedQty", e.target.value)
                          }
                          className="h-[40px] w-full rounded-[10px] border border-[#f2c6cc] px-3 text-[13px] outline-none disabled:bg-slate-50 disabled:text-slate-500"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-[12px] font-semibold text-slate-500">
                          Batch No
                        </label>
                        <input
                          value={split.batchNo}
                          disabled={isReceiptAlreadyReceived}
                          minLength={5}
                          placeholder="Min 5 chars"
                          onChange={(e) =>
                            updateSplit(itemIndex, splitIndex, "batchNo", e.target.value)
                          }
                          className="h-[40px] w-full rounded-[10px] border border-[#f2c6cc] px-3 text-[13px] outline-none disabled:bg-slate-50 disabled:text-slate-500"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-[12px] font-semibold text-slate-500">
                          MFG Date <span className="text-[#d86a78]">*</span>
                        </label>
                        <input
                          type="date"
                          required={!isReceiptAlreadyReceived}
                          value={split.mfgDate}
                          max={getTodayInputDate()}
                          disabled={isReceiptAlreadyReceived}
                          onChange={(e) =>
                            updateSplit(itemIndex, splitIndex, "mfgDate", e.target.value)
                          }
                          className="h-[40px] w-full rounded-[10px] border border-[#f2c6cc] px-3 text-[13px] outline-none disabled:bg-slate-50 disabled:text-slate-500"
                        />
                      </div>

                      <div>
                        <label className="mb-1 block text-[12px] font-semibold text-slate-500">
                          Expiry Date <span className="text-[#d86a78]">*</span>
                        </label>
                        <input
                          type="date"
                          required={!isReceiptAlreadyReceived}
                          value={split.expiryDate}
                          min={split.mfgDate || undefined}
                          disabled={isReceiptAlreadyReceived}
                          onChange={(e) =>
                            updateSplit(itemIndex, splitIndex, "expiryDate", e.target.value)
                          }
                          className="h-[40px] w-full rounded-[10px] border border-[#f2c6cc] px-3 text-[13px] outline-none disabled:bg-slate-50 disabled:text-slate-500"
                        />
                      </div>

                      <div className="flex items-end gap-2">
                        <div className="flex-1">
                          <label className="mb-1 block text-[12px] font-semibold text-slate-500">
                            UOM
                          </label>
                          <input
                            value={split.uom}
                            disabled={isReceiptAlreadyReceived}
                            onChange={(e) =>
                              updateSplit(itemIndex, splitIndex, "uom", e.target.value)
                            }
                            className="h-[40px] w-full rounded-[10px] border border-[#f2c6cc] px-3 text-[13px] outline-none disabled:bg-slate-50 disabled:text-slate-500"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={() => removeSplit(itemIndex, splitIndex)}
                          disabled={item.splits.length === 1 || isReceiptAlreadyReceived}
                          className="inline-flex h-[40px] items-center gap-2 rounded-[10px] border border-[#f2c6cc] px-3 text-[12px] font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 border-t border-[#f8dde1] px-6 py-4">
          <button
            type="button"
            onClick={() =>
              warehouseInventoryService.printInboundReceipt(
                receiptDetail.id,
                receiptDetail.receipt_code || receiptDetail.receiptCode
              )
            }
            className="rounded-[12px] border border-[#f2c6cc] px-5 py-2.5 text-[13px] font-semibold text-slate-600"
          >
            Print GRN
          </button>
          <button
            type="button"
            onClick={() => setReceiptOpen(false)}
            className="rounded-[12px] border border-[#f2c6cc] px-5 py-2.5 text-[13px] font-semibold text-slate-600"
          >
            Close
          </button>
          {!isReceiptAlreadyReceived ? (
            <button
              type="button"
              disabled={receiving}
              onClick={submitReceive}
              className="rounded-[12px] bg-[#d86a78] px-5 py-2.5 text-[13px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
            >
              {receiving ? "Receiving..." : "Receive Stock"}
            </button>
          ) : null}
        </div>
      </div>

      {receiptPopup ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/45 p-4">
          <div className="w-full max-w-lg overflow-hidden rounded-[22px] bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-[#f8dde1] px-5 py-4">
              <div className="flex items-start gap-3">
                <div
                  className={
                    popupType === "success"
                      ? "rounded-full bg-emerald-50 p-2 text-emerald-600"
                      : "rounded-full bg-[#fff0f2] p-2 text-[#d86a78]"
                  }
                >
                  <PopupIcon size={22} />
                </div>
                <div>
                  <h4 className="text-[17px] font-bold text-slate-800">
                    {receiptPopup.title || "Action needed"}
                  </h4>
                  <p className="mt-1 text-[13px] leading-5 text-slate-600">
                    {receiptPopup.message || "Please check the details and try again."}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeReceiptPopup}
                className="rounded-[10px] p-2 text-slate-500 hover:bg-slate-100"
              >
                <X size={17} />
              </button>
            </div>

            {Array.isArray(receiptPopup.items) && receiptPopup.items.length ? (
              <div className="max-h-[260px] overflow-y-auto px-5 py-4">
                <div className="space-y-2">
                  {receiptPopup.items.map((item, index) => (
                    <div
                      key={`${item}-${index}`}
                      className="rounded-[14px] border border-[#f2c6cc] bg-[#fff8f9] px-4 py-3 text-[13px] font-medium leading-5 text-[#b84f5f]"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex justify-end gap-3 border-t border-[#f8dde1] px-5 py-4">
              <button
                type="button"
                onClick={closeReceiptPopup}
                className="rounded-[12px] bg-[#d86a78] px-5 py-2.5 text-[13px] font-semibold text-white hover:bg-[#c95c6a]"
              >
                Okay, I will fix it
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}